<template>
    <div class="page-tabs">
        <el-tabs
            v-model="activePath"
            type="card"
            closable
            @tab-click="handleTabClick"
            @tab-remove="handleTabRemove">
            <el-tab-pane
                v-for="tab in tabs"
                :key="tab.path"
                :label="tab.title"
                :name="tab.path"
                :closable="tab.path !== HOME_PATH" />
        </el-tabs>
    </div>
</template>

<script setup>
import { nextTick, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const STORAGE_KEY = "admin_page_tabs";
const HOME_PATH = "/dashboard";
const HOME_TAB = { path: HOME_PATH, routePath: HOME_PATH, title: "\u9996\u9875" };
const route = useRoute();
const router = useRouter();
const tabs = ref(readTabs());
const activePath = ref("");

function readTabs() {
    try {
        const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
        return normalizeTabs(Array.isArray(value) ? value : []);
    } catch (e) {
        return [HOME_TAB];
    }
}

function normalizeTabs(sourceTabs) {
    const otherTabs = [];
    const usedPaths = new Set([HOME_PATH]);
    sourceTabs.forEach((item) => {
        if (!item || !item.path || item.path === "/login" || item.path === "/mainBox" || item.path === HOME_PATH) {
            return;
        }
        if (usedPaths.has(item.path)) {
            return;
        }
        usedPaths.add(item.path);
        otherTabs.push({
            path: item.path,
            routePath: item.routePath || item.path,
            title: item.title === "\u63a7\u5236\u53f0" ? HOME_TAB.title : item.title,
        });
    });
    return [HOME_TAB, ...otherTabs];
}

function saveTabs() {
    tabs.value = normalizeTabs(tabs.value);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs.value));
}

function getTitle(currentRoute) {
    return currentRoute.path === HOME_PATH ? HOME_TAB.title : (currentRoute.meta?.title || currentRoute.name || currentRoute.path);
}

function addCurrentTab(currentRoute) {
    if (!currentRoute.path || currentRoute.path === "/login" || currentRoute.path === "/mainBox") {
        return;
    }
    const tab = currentRoute.path === HOME_PATH ? HOME_TAB : {
        path: currentRoute.fullPath,
        routePath: currentRoute.path,
        title: getTitle(currentRoute),
    };
    const exists = tabs.value.some((item) => item.path === tab.path);
    if (!exists) {
        tabs.value.push(tab);
    }
    saveTabs();
    activePath.value = tab.path;
}

function handleTabClick(tab) {
    if (tab.props.name && tab.props.name !== route.fullPath) {
        router.push(tab.props.name);
    }
}

function handleTabRemove(path) {
    if (path === HOME_PATH || tabs.value.length <= 1) {
        return;
    }
    const index = tabs.value.findIndex((item) => item.path === path);
    if (index === -1) {
        return;
    }
    tabs.value.splice(index, 1);
    saveTabs();
    if (path === route.fullPath) {
        const nextTab = tabs.value[index] || tabs.value[index - 1] || tabs.value[0];
        if (nextTab) {
            nextTick(() => router.push(nextTab.path));
        }
    }
}

watch(
    () => route.fullPath,
    () => addCurrentTab(route),
    { immediate: true }
);
</script>

<style lang="scss" scoped>
.page-tabs {
    height: 40px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    padding: 6px 12px 0;
    box-sizing: border-box;
    overflow: hidden;

    :deep(.el-tabs__header) {
        margin: 0;
        border-bottom: none;
    }

    :deep(.el-tabs__nav) {
        border: none;
    }

    :deep(.el-tabs__item) {
        height: 32px;
        line-height: 32px;
        border: 1px solid #dcdfe6;
        margin-right: 6px;
        color: #5f6673;
        background: #f8fafc;
    }

    :deep(.el-tabs__item.is-active) {
        color: var(--admin-theme-color, #2d3a4b);
        background: #fff;
        border-color: var(--admin-theme-color, #2d3a4b);
        border-bottom-color: #fff;
        font-weight: 600;
    }
}
</style>
